import { makeStyles } from '@material-ui/core/styles';

/**
 * The approvals queue and the offer review panel (AXHE-3947).
 *
 * Spacing is on the 2px grid — 16px default, 8px inside the dense approver rows,
 * and the two are never mixed inside one region. Colours come from the FDS theme
 * palette; no raw hex.
 */
export const useStyles = makeStyles(theme => ({
  // --- the toolbar (AXHE-3934) ---

  // One row: the filters at the left edge, the search box at the right one. The
  // two are read for different reasons — the filters narrow a queue somebody is
  // scanning, the box finds a row they already have in mind — and the table's
  // full width between them is what says so.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  // 8px between the three controls, not the page's 16px: they are one control
  // group, and spacing them like separate blocks reads as three unrelated
  // buttons. The two densities never meet.
  toolbarFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  // A pill, as the design has it. `borderRadius: 999` so the shape survives the
  // height changing.
  filterPill: {
    borderRadius: 999,
    textTransform: 'none',
    height: 28,
    padding: '0 12px',
    borderColor: theme.palette.common.gray300,
    color: theme.palette.text.primary,
    // `theme.typography.button` is 16px at a numeric 500 on the Book face — a
    // step above every other line on this page, and a faux-bold rather than the
    // Medium face. A filter that reads larger than the rows it filters inverts
    // the hierarchy, so these take the `body2` role the table headers and the
    // row text use.
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.body2.fontFamily,
    '& .MuiButton-endIcon': {
      marginLeft: 4,
    },
    // The caret is punctuation on a 28px control, not an icon beside a label.
    '& .MuiButton-endIcon > *:first-child': {
      fontSize: 10,
    },
    // Stated outright: MUI's `ButtonBase` sets `outline: 0` unconditionally and
    // ships an empty `focusVisible` class, and this theme overrides neither, so
    // a keyboard user tabbing across the toolbar otherwise gets nothing. The
    // same ring `nameButton` and `previewLink` below write for themselves.
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    // One opacity over the whole control, which is how the FDS button these
    // pills used to be mutes itself. MUI paints a disabled button's label and
    // border from two-class rules of its own, which outrank a single class here
    // whatever the sheet order — so the pill's colours are restated at the same
    // specificity. Left to MUI, an active pill held through a bulk run keeps its
    // blue50 fill under a near-invisible gray label.
    '&&.Mui-disabled': {
      color: theme.palette.text.primary,
      borderColor: theme.palette.common.gray300,
      opacity: 0.4,
    },
  },
  // Tinted AND labelled: the button's own text carries the chosen value, so the
  // tint reinforces rather than being the only signal.
  filterPillActive: {
    background: theme.palette.common.blue50,
    borderColor: theme.palette.common.blue600,
    color: theme.palette.common.blue600,
    '&:hover': {
      background: theme.palette.common.blue50,
    },
    // Held through a bulk run the pill is still the active one, so it keeps its
    // own colours and takes only the opacity `filterPill` puts over everything.
    '&&.Mui-disabled': {
      color: theme.palette.common.blue600,
      borderColor: theme.palette.common.blue600,
    },
  },
  // 150px so the menu is wider than the 28px pill that opens it, which is what
  // stops a two-word entry wrapping in a popover the reader has to keep open to
  // read. The radius is the 4 every surface in this file uses rather than the
  // design's 6, and the elevation is MUI's own — a shadow written here would be
  // a raw rgba this palette does not carry.
  filterMenuPaper: {
    minWidth: 150,
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: 4,
  },
  // A 36px row: 8px above and below an explicit 20px line. The line-height is
  // stated rather than left to the font's own `normal`, which is what the row
  // would otherwise be measured by — and that moves with the fallback face while
  // the web font is still loading. MUI's `minHeight` is already `auto` above the
  // `sm` breakpoint, so nothing else is holding the height.
  filterMenuItem: {
    padding: '8px 14px',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: '20px',
    // MUI's whole focus cue for a menu row is a fill, and `ButtonBase` clears
    // the outline that would otherwise stand in for it. The fill is `#ebebeb`
    // on white, 1.19:1, which is under the 3:1 a focus indicator owes — and on
    // the selected row it is the same fill the rule below removes. An outline
    // carries it instead, inset so the paper's own edge does not clip it.
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  // The chosen entry reads as chosen through weight and colour rather than
  // through MUI's default gray fill, which on a menu of four is a swatch behind
  // one line rather than a mark on it.
  //
  // Three classes deep, not two. `ListItem` paints that fill from
  // `&$selected`, which compiles to `.MuiListItem-root.Mui-selected` — also two
  // classes, so a doubled selector only ties it and leaves the winner to
  // whichever sheet the bundler inserted last. It resolves one way under the
  // dev server and the other in the built app.
  //
  // `primary.main`, the blue every other link in this app is written in. The
  // darker blue600 beside it is the selected-chip token and belongs to the pill,
  // not to a line of text.
  filterMenuItemSelected: {
    '&&&': {
      background: 'none',
      color: theme.palette.primary.main,
      fontFamily: theme.typography.h4.fontFamily,
    },
    // The pointer still gets an answer on this row. `ListItem` emits its
    // selected fill for `:hover` as well, at three classes — the same weight as
    // the rule above, so that one only ties it. The pseudo-class here outranks
    // it, and puts back the ordinary hover the reset would otherwise take away
    // from the one row in the menu that has it.
    '&&&:hover': {
      background: theme.palette.action.hover,
    },
  },
  filterMenuCheck: {
    fontSize: 14,
    marginLeft: 8,
  },
  emptyAction: {
    marginTop: 8,
  },

  // At the right edge of the toolbar, at a fixed 240px. It does not grow to fill
  // the gap between itself and the filters: a box that stretches to 500px on a
  // wide screen reads as the page's primary control, and the queue's is the
  // table. 240px is wide enough for the placeholder it has to show, which is
  // what sets the floor.
  searchField: {
    flex: '0 0 auto',
    width: 240,
    '& .MuiOutlinedInput-root': {
      height: 36,
      background: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.common.gray300,
    },
  },
  searchIcon: {
    fontSize: 16,
    color: theme.palette.common.gray600,
  },
  searchClear: {
    color: theme.palette.common.gray600,
    // Pulls the target back so the glyph sits on the field's own inset.
    marginRight: -6,
    // Stated outright, as `nameButton` and `previewLink` above state their
    // own: MUI's `ButtonBase` sets `outline: 0` unconditionally and ships an
    // empty `focusVisible` class, so an `IconButton` has no focus indicator
    // at all until one is written.
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },

  // Under the table, replacing the "Showing N of M" line the capped list carried.
  pagination: {
    marginTop: 16,
  },
  // ---- the queue ----
  // Matches the rule table above it: the Position column holds account-authored
  // text of a length this page does not choose, so the container scrolls rather
  // than the columns crushing.
  list: {
    marginTop: 16,
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: 4,
    overflowX: 'auto',
  },
  table: {
    tableLayout: 'auto',
  },
  // Matched to the rule table's own header row on the adjacent tab: same band,
  // same height, same divider, same secondary colour, same medium face. Two
  // tables on one page reading at different densities is the "inconsistent
  // density" the guard names. `ApprovalRules/styles.ts` carries the reasoning
  // for the band colour and for taking the weight from the family.
  headerCell: {
    padding: '0 14px',
    height: 40,
    background: theme.palette.common.gray100,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.h4.fontFamily,
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
  },
  // Loading and empty both render inside the table body, so the column headers
  // stay put and the region does not change shape twice per load.
  stateCell: {
    textAlign: 'center',
    padding: 32,
    borderBottom: 'none',
  },
  empty: {
    textAlign: 'center',
    padding: 32,
    borderBottom: 'none',
  },
  emptyBody: {
    marginTop: 8,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    marginTop: 16,
    border: `1px solid ${theme.palette.common.red400}`,
    background: theme.palette.common.red100,
    borderRadius: 4,
  },
  // ---- bulk selection (AXHE-3934) ----
  // Between the toolbar and the table, not floating over it: a pinned bar covers
  // the last row of the page, which is a row the reader may be about to tick.
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 16,
    padding: '9px 14px',
    background: theme.palette.common.blue50,
    // The tint is what marks the region out; the border only closes it. A
    // saturated edge here competes with the primary button inside the bar for
    // the same eye, and the bar is the container, not the action.
    border: `1px solid ${theme.palette.common.brandTintEdge}`,
    borderRadius: 8,
    // The table owns a standing 16px gap to whatever precedes it, which is right
    // when the bar is absent and 4px too loose when it is not — the bar and the
    // rows it acts on are one unit.
    //
    // Written onto the table rather than as a `marginBottom` here, because that
    // margin would meet the table's own and collapse to the larger of the two,
    // leaving the gap at 16. Two classes beat one, so this overrides `list`
    // outright, and it keeps doing so if the bar ever gains a bottom margin.
    // `bulkSummary`, which takes this slot once a run has finished, deliberately
    // keeps the full 16: it reports on the page like the load error above it
    // rather than acting on the rows below.
    '& + $list': {
      marginTop: 12,
    },
  },
  // The number is the heading here, not a detail beside the buttons — the reader
  // is about to decide several things at once. The medium face rather than a
  // numeric weight, for the reason `nameButton` below gives.
  //
  // Doubled, unlike the colour-only overrides elsewhere in this file. Those
  // never collide, because an MUI variant class sets no `color`. This one takes
  // `font-family` and `line-height` off `.MuiTypography-body2`, which does set
  // both — and at one class each the two only tie, leaving the winner to
  // whichever sheet the bundler inserted last. `selectCell` below records the
  // same trap.
  bulkCount: {
    '&&': {
      ...theme.typography.body2,
      lineHeight: 1.5,
      fontFamily: theme.typography.h4.fontFamily,
      color: theme.palette.common.gray800,
    },
  },
  bulkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  // What a finished bulk run came to. Above the table, like the load error, since
  // it describes the page rather than a row.
  bulkSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 4,
    background: theme.palette.common.gray100,
    border: `1px solid ${theme.palette.common.gray300}`,
  },
  // Narrow, and no padding of its own: a checkbox column that reserves a cell's
  // width pushes the Opening column off the reader's left edge.
  //
  // Doubled selector because a body cell carries `cell` as well, whose shorthand
  // `padding` is emitted later in this sheet and resets this longhand at equal
  // specificity. The header's cell pairs with `headerCell` instead, which is
  // emitted earlier — so a single class here holds in the header and loses in
  // the body, putting every row's checkbox 14px left of the one above it.
  selectCell: {
    width: 52,
    '&&': {
      paddingRight: 0,
    },
  },
  // Every body cell. The theme clears `MuiTableCell`'s own bottom border, so
  // this is what puts a rule between one request and the next — and a row here
  // is three stacked lines deep, which is where an eye tracking down the column
  // needs one to land. 12px vertical rather than 16: the rows are scanned in
  // bulk, and `layout-composition` puts that density at the dense step.
  //
  // Not under the last row, which sits one pixel above the container's own
  // border and would read as a double rule.
  cell: {
    padding: '12px 14px',
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  // Offer rows only. An opening row is decided from its own cell and opens
  // nothing, so a pointer over it would promise a click that does nothing —
  // Design Requirements §5 locks that asymmetry.
  rowClickable: {
    cursor: 'pointer',
  },
  // The opening name on an offer row, as the design has it: brand blue and
  // medium weight, so the first cell reads as the way into the offer. A real
  // `<button>` rather than a `role="button"` on the `<tr>` — the row also holds
  // a checkbox and the Review control, and nesting those inside a widget is an
  // axe `nested-interactive` violation.
  //
  // The first column's three lines: the name, the applicant under it, and the
  // preview link under that. `body2` sets a 1rem line-height, which on three
  // stacked lines is one solid block rather than a name with detail beneath it,
  // so the cell opens them up to 1.5.
  subjectCell: {
    '& > *': {
      lineHeight: 1.5,
    },
  },
  // The name that is not a link, on an opening row. Same size and face as the
  // button beside it on an offer row — the two sit in one column, and a plain
  // name at a different size would read as a different kind of thing.
  nameText: {
    ...theme.typography.body2,
    fontFamily: theme.typography.h4.fontFamily,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  },
  // Size and family come from `body2`, matching `nameText` above.
  nameButton: {
    ...theme.typography.body2,
    // `body2` sets a 1rem line-height, and its rule is emitted after
    // `subjectCell`'s, so the opening-up has to be repeated here.
    lineHeight: 1.5,
    // The medium face, not `fontWeight: 500`. This theme carries weight in the
    // family — `CircularXXWeb-Medium` is a family of its own, and `h4` is the
    // role that uses it — so asking for a numeric weight would leave the browser
    // synthesizing a faux-bold out of the Book face.
    fontFamily: theme.typography.h4.fontFamily,
    border: 0,
    margin: 0,
    padding: 0,
    background: 'transparent',
    textAlign: 'left',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  },
  // Under the applicant line, third in the cell. Same link-styled button as
  // `historyToggle` in the panel — one treatment for a text link in this
  // feature.
  previewLink: {
    ...theme.typography.body2,
    // `inline-flex`, not `flex`: a block-level button in a table cell stretches
    // to the column's width, which would put an invisible click target across
    // the whole cell. The applicant line above it is a block, so it still starts
    // its own line.
    display: 'inline-flex',
    alignItems: 'center',
    // 4px is the icon-to-label gap everywhere; the 8px this container uses
    // between controls would read as two separate things.
    gap: 4,
    marginTop: 4,
    padding: 0,
    border: 0,
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textAlign: 'left',
    // The third of the cell's three lines. `body2` carries a 1rem line-height
    // and its rule is emitted after `subjectCell`'s, so the opening-up has to be
    // repeated here as it is on the two lines above.
    lineHeight: 1.5,
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  },
  actionsCell: {
    // Sized to hold Reject beside Approve: the two FDS small buttons measure 73
    // and 87 with 8px between them, 168 in total, and `cell`'s own 28px of
    // horizontal padding leaves 180 of this 208. Below that the pair wraps, and
    // a wrapped pair stands the destructive control directly above the primary
    // one.
    //
    // The column carries the width rather than the buttons being shrunk into it.
    // 30px and 13px is what the design draws, but `size="small"` is the design
    // system's own small button and a bespoke smaller one here would be the only
    // one in the app.
    //
    // 12px of the 180 is slack, so a locale whose two labels are longer than the
    // English pair still wraps — `rowActions` keeps `flex-wrap` for that.
    //
    // The Review control on an offer row is narrower, and one width keeps the
    // column edge straight down the table.
    width: 208,
    textAlign: 'right',
  },
  // Approve and Reject on one line, right-aligned with the column. Wraps rather
  // than overflowing: the labels are translated, and a locale that needs two
  // lines gets them.
  rowActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 8,
  },
  // An approval the server refused. Under the buttons rather than beside them —
  // it is a sentence, and the cell is the narrowest on the row.
  rowActionError: {
    marginTop: 4,
    color: theme.palette.common.red600,
    textAlign: 'right',
  },
  // Names the actions column for a screen reader reading the table column by
  // column, without putting a heading on screen the design does not have. Same
  // rule the rule table above it uses.
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // One line per rule in the Approver rule column. Stacked rather than
  // comma-joined: several rules gate one subject, and the cell beside this lists
  // their levels in the same order, so the two read across row by row.
  // The rule name is this column's value, not a caption on one, so it takes the
  // primary text tier rather than `body2`'s own secondary gray. Read across
  // against the levels in the column beside it, which are the same size.
  ruleNames: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    '& > *': {
      color: theme.palette.text.primary,
      lineHeight: 1.5,
    },
  },

  // ---- the Approval status column ----
  // One line per level. 4px, not the page's 16px: the lines are one answer read
  // top to bottom, and spacing them further apart reads as separate facts.
  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  // The glyph, the level, and what it came to, on one line. Wraps rather than
  // truncating — a named level plus its state is longer than this column wants to
  // be, and the state is the half a reader came for.
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  // Reinforcement, never the signal: the state's own word sits beside it. Neutral
  // by default, so only a finished level is tinted at all.
  statusIcon: {
    fontSize: 16,
    flexShrink: 0,
    color: theme.palette.common.gray600,
  },
  // green600 and red600 on white, not on a tint — these are glyphs on the table's
  // own background, where the mid-tone tokens clear AA and the pill pairings do
  // not apply.
  statusIconApproved: {
    color: theme.palette.common.green600,
  },
  statusIconRejected: {
    color: theme.palette.common.red600,
  },
  // The level's name at full strength and its state secondary: the reader scans
  // the column for which level, then reads across for what happened.
  statusLevel: {
    color: theme.palette.text.primary,
  },
  // A level nobody can clear. The error role, because it is a misconfiguration a
  // person has to fix rather than a state anybody chose.
  statusAttention: {
    color: theme.palette.common.red600,
  },

  // ---- the review panel ----
  // The Applicant Table's offer preview width (SendOffer's `drawerPreview`): the
  // offer preview is one drawer at one width on every page. MUI orders sheets
  // by when `makeStyles` runs, and the FDS Drawer's runs first, so this width
  // also overrides its 100% below `sm`. The vw cap is what keeps the panel and
  // its footer controls inside a narrow window.
  drawer: {
    width: 'min(1160px, 100vw)',
  },
  // The edit form has no letter beside it, so it takes a form's width rather
  // than the preview's.
  drawerEdit: {
    width: 'min(640px, 100vw)',
  },
  // The panel opens with no top edge otherwise — the same fix SendOffer's drawer
  // applies, and for the same reason: `DrawerHeader` accepts `showShadow` and
  // then ignores it.
  panelHeader: {
    borderBottom: `1px solid ${theme.palette.common.gray300}`,
  },
  panelBody: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 24,
    padding: 24,
  },

  // ---- the approver rail ----
  // 240px, the prototype's width and the one `SendOffer/OfferPreview`'s rail
  // already carries, so both drawers draw the shared chain in one column. Fixed
  // rather than flexing: the levels and their approvers are the reference
  // column, and letting it flex would trade a stable reading position for a few
  // pixels of letter width.
  rail: {
    width: 240,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  railSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // Earlier attempts at this offer. Collapsed by default: the current submission
  // is what an approver is being asked about, and on a first submission there is
  // nothing here at all.
  historyToggle: {
    ...theme.typography.body2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    background: 'none',
    border: 0,
    color: theme.palette.common.blue600,
    cursor: 'pointer',
    textAlign: 'left',
  },
  submission: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 8,
  },
  submissionReason: {
    color: theme.palette.common.gray800,
    fontStyle: 'italic',
    overflowWrap: 'anywhere',
  },
  warningIcon: {
    fontSize: 16,
    color: theme.palette.common.orange600,
    flexShrink: 0,
    // Aligns the glyph to the first line's mid-point rather than to its box.
    marginTop: 2,
  },

  // ---- the letter ----
  // The grey backdrop the design puts the letter on, so a white page reads as a
  // document sitting on a surface rather than as the panel's own background.
  letter: {
    flexGrow: 1,
    minWidth: 0,
    background: theme.palette.common.gray200,
    borderRadius: 4,
    padding: 16,
  },
  letterFrame: {
    width: '100%',
    // Fills the drawer's vertical space instead of a fixed height — giving the
    // letter room to read is the point of a panel this wide.
    height: '70vh',
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: 4,
    background: theme.palette.common.white,
    display: 'block',
  },
  letterMissing: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: 24,
    background: theme.palette.common.white,
    borderRadius: 4,
  },

  // ---- the decision footer ----
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    width: '100%',
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
  },
  footerNote: {
    color: theme.palette.common.gray600,
  },
  footerError: {
    color: theme.palette.common.red600,
  },
  footerConfirmed: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.common.green600,
  },
  // The reason field takes the footer's full width: it is a sentence somebody
  // reads later, and a field sized to sit beside the buttons would show two
  // words of it.
  reasonField: {
    width: '100%',
  },
  /**
   * The label on every destructive control the queue renders: the row's Reject,
   * the bulk bar's Reject, and both steps of the offer panel's rejection.
   *
   * FDS paints a `secondary negative` button's text in `common.error` (red400),
   * which computes to 4.4987:1 on white — under AA's 4.5 for text. red600 is
   * 6.12:1 and reads as the same destructive control, so only the label moves;
   * the red400 box stays, and it clears the 3:1 a border has to meet. That
   * red600-label-on-red400-border pairing is what the design specifies.
   *
   * All four together, not the confirm alone. Two of them are the same word one
   * click apart — the offer panel's Reject opens the reason field, and the
   * control that then records it carries the same label in the same footer — so
   * a shade that moved on one of them would read as two different reds for one
   * action.
   *
   * Doubled selector because FDS sets that color from a props function, which
   * JSS emits into its dynamic sheet — a single-class rule here would tie it
   * and lose on sheet order. `:not(.Mui-disabled)` hands the disabled state
   * back to FDS rather than painting a washed-out button red.
   *
   * Scoped to this surface rather than fixed in `Buttons/Button/styles.ts`:
   * that token is the design system's, and repainting every destructive button
   * in every app is its own change.
   */
  destructiveButton: {
    '&&:not(.Mui-disabled)': {
      color: theme.palette.common.red600,
    },
  },
  /**
   * Full strength while the decision is in flight, on the same outlined confirm.
   *
   * FDS dims a disabled `secondary` to `opacity: 0.4` and a disabled `primary`
   * not at all. The button is disabled for two different reasons — no reason
   * typed yet, and a decision already sent — and only the first is "unavailable".
   * The second swaps the label for a spinner, and 0.4 leaves that spinner the
   * one cue somebody is waiting on at 40%.
   *
   * So the dim is lifted for the in-flight case only, which is why this is a
   * second class the component adds rather than a rule inside the one above:
   * `:disabled` cannot tell the two reasons apart, and React can.
   */
  destructiveSubmitting: {
    '&&.Mui-disabled': {
      opacity: 1,
    },
  },
  /**
   * The footer's two groups (Design Requirements §6): editing on the left,
   * deciding on the right.
   *
   * `space-between` with the left slot always rendered — as an empty div when
   * there is no edit control — so the decision buttons keep the same right-hand
   * position whether or not the reader may edit. A left slot that appears and
   * disappears would move Approve under the cursor of somebody who has just
   * been shown a different footer.
   */
  footerGroups: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  footerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  // ---- editing the offer (AXHE-4759) ----
  // The form replaces the letter and the rail both, so it gets the panel's full
  // width. 24px matches `panelBody`, which it stands in for.
  editBody: {
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  /**
   * The three overrides `InlineMessage` needs for a message that wraps.
   *
   * Its root is a MUI v4 `Grid container` at `width: 100%`, it hardcodes
   * `variant="h4"` (14px on a 16px line in this theme), and its icon is an
   * `<svg>` with no intrinsic height inside an `align-items: stretch` row. On
   * one line none of that shows; on the two sentences this banner carries, the
   * copy sets tighter than everything around it and the warning glyph stretches
   * to the full block height, centered against the middle of the paragraph
   * instead of the first line.
   */
  editWarning: {
    '& .MuiTypography-root': {
      lineHeight: '20px',
    },
    '& img': {
      alignSelf: 'flex-start',
      height: 16,
      marginTop: 2,
    },
  },
  // The approval notice between the form and its footer. It renders outside the
  // drawer's content column, so it carries that column's gutter (the Drawer's
  // own, narrower below `sm`) plus the edit body's 24px, and lines up with the
  // fields at every width.
  editApprovalNotice: {
    padding: theme.spacing(0, 8, 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 6, 2),
    },
  },
  // The notice's second sentence on its own line under the title, in the notice's
  // own color, as the drawer's re-approval notice sets it.
  editWarningBody: {
    color: 'inherit',
    display: 'block',
    marginTop: 4,
  },
  // The readiness count and any server refusal, above the controls they explain
  // — the same placement the decision footer gives its own error, and for the
  // same reason: inline they would either clip or push the buttons off the edge.
  editFooterNote: {
    color: theme.palette.common.gray600,
  },
}));
